import { useCallback, useState } from 'react'

import { Button } from 'antd'
import { HStack, Icon, Tooltip } from 'govwell-ui'
import { ScrapeTemplateFragment } from 'types/graphql'

import { DeleteButton } from 'src/components/TableV2/DeleteButton'
import { EditButton } from 'src/components/TableV2/EditButton'
import { useStartScrapeNowMutation } from 'src/fetch/scrapes'
import { useDeleteScrapeTemplateMutation } from 'src/fetch/scrapeTemplates'
import ConfirmationModal from 'src/govwell-ui/components/Modal/ConfirmationModal'
import useDisclosure from 'src/hooks/use-disclosure'
import { RefetchFnType } from 'src/types'

import ScrapeTemplateModal from './ScrapeTemplateModal'

interface Props {
  scrapeTemplate: ScrapeTemplateFragment
  refetch: RefetchFnType
}

const ScrapeTemplatesTableButtons = ({ scrapeTemplate, refetch }: Props) => {
  const editModal = useDisclosure()
  const deleteConfirmationModal = useDisclosure()
  const startConfirmationModal = useDisclosure()
  const [isDeleting, setIsDeleting] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const { mutateAsync: deleteScrapeTemplate } = useDeleteScrapeTemplateMutation()
  const { mutateAsync: startScrapeNow } = useStartScrapeNowMutation()

  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await deleteScrapeTemplate({ id: scrapeTemplate.id })
    } catch (error) {
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }, [scrapeTemplate.id, deleteScrapeTemplate])

  const handleDeleteClosed = useCallback(async () => {
    await refetch()
  }, [refetch])

  const handleStartScrapeNow = useCallback(async () => {
    setIsStarting(true)
    try {
      await startScrapeNow({ scrapeTemplateId: scrapeTemplate.id })
    } catch (error) {
      console.error(error)
    } finally {
      setIsStarting(false)
    }
  }, [scrapeTemplate.id, startScrapeNow])

  const handleStartScrapeNowClosed = useCallback(async () => {
    await refetch()
  }, [refetch])

  return (
    <>
      <HStack gap={2} justify="end">
        <Tooltip content="Start scrape now">
          <Button
            icon={<Icon.FaPlay />}
            type="default"
            size="small"
            onClick={(e) => {
              e?.preventDefault()
              startConfirmationModal.open()
            }}
          />
        </Tooltip>

        <EditButton onClick={editModal.open} tooltip="Edit" />
        <DeleteButton onClick={deleteConfirmationModal.open} tooltip="Delete" />
      </HStack>

      {editModal.isOpen && (
        <ScrapeTemplateModal
          onClose={editModal.close}
          existingScrapeTemplate={scrapeTemplate}
          refetch={refetch}
        />
      )}

      {startConfirmationModal.isOpen && (
        <ConfirmationModal
          title="Start"
          onCancel={startConfirmationModal.close}
          onConfirm={handleStartScrapeNow}
          onClosed={handleStartScrapeNowClosed}
          isLoading={isStarting}
          confirmText="Start"
          cancelText="Cancel"
        >
          Start scraping {scrapeTemplate.startUrl}?
        </ConfirmationModal>
      )}

      {deleteConfirmationModal.isOpen && (
        <ConfirmationModal
          title="Delete"
          onCancel={deleteConfirmationModal.close}
          onConfirm={handleDelete}
          onClosed={handleDeleteClosed}
          isLoading={isDeleting}
          isDestructive={true}
          confirmText="Delete"
          cancelText="Cancel"
        >
          Are you sure you want to delete &quot;{scrapeTemplate.startUrl}&quot;? This action cannot
          be undone.
        </ConfirmationModal>
      )}
    </>
  )
}

export default ScrapeTemplatesTableButtons
